<template id="app">
	<v-app
		:style="{ 'background-color': bg_color, height: `${window_height}px` }"
	>
		<template v-if="user == null">
			<template v-if="alert">
				<v-alert
					:value="!!alert.message"
					transition="scale-transition"
					:type="alert.type"
					dark
					dense
					class="rounded-0 text-center"
					ref="alert"
					:icon="false"
				>
					{{ alert.message }}
					<template v-if="alert.link">
						<a @click="$router.push(alert.link)">
							{{ alert.label }}
						</a>
					</template>
					<v-icon
						class="float-right"
						transition="scale-transition"
						@click="removeAlert"
						>mdi-close</v-icon
					>
				</v-alert>
			</template>
		</template>

		<v-navigation-drawer
			v-model="drawer"
			:color="theme.primary"
			dark
			floating
			:elevation="12"
			app
			:width="350"
			v-if="user"
		>
			<v-list dense class="py-0" dark>
				<v-list-item class="py-8 pl-5" two-line :class="miniVariant && 'px-0'">
					<v-list-item-avatar
						:size="50"
						tile
						class="rounded-lg cursor-pointer"
						:color="theme.accent"
						@click="goTo('Profile')"
					>
						<template v-if="user.image">
							<v-img :src="user.image" :alt="`[${user.name}]`" />
						</template>
						<template v-else>
							<span class="white--text subheading">{{ getInitials() }}</span>
						</template>
					</v-list-item-avatar>
					<v-list-item-content
						class="ml-2 cursor-pointer"
						@click="goTo('Profile')"
					>
						<div class="uppercase bold font-small">
							{{ user.username }}
							<v-icon
								class="pa-1 ml-2 rounded-circle"
								:style="{ 'background-color': user.level.color }"
								:color="'white'"
								:size="16"
								>star</v-icon
							>
						</div>
					</v-list-item-content>
				</v-list-item>

				<v-divider class="pb-3 pt-2"></v-divider>

				<v-list-item
					v-for="item in navItems"
					:key="item.title"
					@click="goTo(item.route)"
					:class="{
						'nav-active': navIsActive(item.route),
					}"
					:style="{
						'background-color': navIsActive(item.route)
							? `${theme.secondary} !important`
							: `${theme.primary} !important`,
						'border-left': navIsActive(item.route)
							? `3px solid ${theme.accent}`
							: `0`,
					}"
					link
					class="nav-item py-4"
				>
					<v-list-item-icon class="pl-2">
						<v-icon
							:class="{
								'col-red': item.route == 'logout',
							}"
							>{{ item.icon }}</v-icon
						>
					</v-list-item-icon>

					<v-list-item-content>
						<v-list-item-title
							:class="{
								'col-red': item.route == 'logout',
							}"
							>{{ item.title }}</v-list-item-title
						>
					</v-list-item-content>
				</v-list-item>
			</v-list>
			<v-hover
				class="cursor-pointer position-absolute bottom bottom-0 mt-5"
				v-show="true"
			>
				<template v-slot="{ hover }" v-if="show_keepaway">
					<div :class="`elevation-${hover ? 14 : 1}`" class="transition-swing">
						<v-card @click="startKeepAway">
							<v-alert
								transition="slide-y-transition"
								:type="
									keep_away_active ? (in_danger ? 'error' : 'success') : 'info'
								"
								dark
								dense
								:dismissible="false"
								class="rounded-0 text-center cursor-pointer"
								ref="alert"
								:icon="false"
							>
								<span
									class="float-left font-under-normal"
									v-if="keep_away_active == false"
								>
									Start Keepaway
								</span>
								<template v-else>
									<span class="float-left font-under-normal" v-if="in_danger">
										You are too close to another device!
									</span>
									<span class="float-left font-under-normal" v-else>
										Keepaway is active
									</span>
								</template>
								<v-icon
									class="float-right"
									transition="scale-transition"
									@click="removeAlert"
									>{{
										keep_away_active
											? in_danger
												? 'mdi-alert'
												: 'mdi-check'
											: 'mdi-chevron-right'
									}}
								</v-icon>
							</v-alert>
						</v-card>
					</div></template
				></v-hover
			>
		</v-navigation-drawer>

		<v-main class="main" :style="{ 'background-color': bg_color }">
			<template v-if="user">
				<template v-if="alert">
					<v-alert
						:value="!!alert.message"
						transition="slide-y-transition"
						:type="alert.type"
						dark
						dense
						class="rounded-0 text-center"
						ref="alert"
						:icon="false"
					>
						{{ alert.message }}
						<template v-if="alert.link">
							<a @click="$router.push(alert.link)">
								{{ alert.link_label }}
							</a>
						</template>
						<v-icon
							class="float-right"
							transition="scale-transition"
							@click="removeAlert"
							>mdi-close</v-icon
						>
					</v-alert>
				</template>
			</template>
			<v-container fluid fill-height class="pt-10">
				<v-row class="fill-height">
					<v-col>
						<div class="float-right" v-if="user">
							<v-switch
								@change="toggleTheme"
								class="mx-2 font-small"
								:color="theme.accent"
								:dark="theme.dark"
								label="Dark mode"
								:value="dark_mode_active"
							></v-switch>
						</div>
						<h1
							class="display-2 font-weight-bold mb-3 ml-6"
							v-if="user"
							:class="theme.dark && 'col-white'"
						>
							{{ page_title }}
						</h1>
						<v-breadcrumbs v-if="user">
							<v-icon color="grey" @click="goTo('Dashboard')">home</v-icon>
							<breadcrumbs :class="theme.dark && 'col-white'" />
						</v-breadcrumbs>
						<transition name="scale">
							<!--fade-->
							<router-view class="fade"></router-view>
						</transition>
					</v-col>
				</v-row>
			</v-container>
		</v-main>
		<v-footer id="footer" :class="theme.dark && 'bg-black'" v-if="user">
			<div class="ml-auto uppercase font-small bold">
				© Enoch Banjo
			</div>
		</v-footer>
		<v-overlay v-if="loading || force_load">
			<half-circle-spinner
				:animation-duration="1000"
				:size="60"
				color="white"
			/>
		</v-overlay>
	</v-app>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { HalfCircleSpinner } from 'epic-spinners'
export default {
	name: 'App',
	components: {
		HalfCircleSpinner,
	},
	data: () => ({
		drawer: true,
		navItems: [
			{ title: 'Dashboard', icon: 'dashboard', route: 'Dashboard' },
			{ title: 'My profile', icon: 'mdi-account', route: 'Profile' },
			{ title: 'Help center', icon: 'mdi-help-box', route: 'Help Center' },
			{ title: 'About', icon: 'mdi-information-variant', route: 'About' },
			{ title: 'Logout', icon: 'mdi-exit-to-app', route: 'logout' },
		],
		color: 'primary',
		colors: ['primary', 'blue', 'success', 'red', 'teal'],
		right: false,
		permanent: true,
		miniVariant: false,
		expandOnHover: false,
		background: false,
		window_height: window.innerHeight,
		alert_height: 0,
		nearby_devices: {},
		keep_away_active: false,
		in_danger: false,
		dark_mode_active: false,
	}),
	methods: {
		...mapMutations(['setAlert', 'logout', 'removeAlert', 'toggleTheme']),

		goTo(route) {
			if (route == 'logout') return this.innerLogout()
			if (this.$route.name == route) return
			this.$router.push({ name: route })
		},

		innerLogout() {
			this.removeAlert()
			this.core.http
				.post('auth/logout')
				.then(() => {
					this.logout()
				})
				.catch(error => {
					this.setAlert(error.response)
				})
		},

		navIsActive(route) {
			return (
				this.$route.name == route ||
				(['Course', 'Quiz'].includes(this.$route.name) && route == 'Dashboard')
			)
		},

		getInitials() {
			return this.user.name
				.split(' ')
				.map(w => w[0])
				.join('')
				.toUpperCase()
		},

		async startKeepAway() {
			if (this.show_keepaway == false) return
			const danger_rssi = process.env.VUE_APP_KA_DANGER_RANGE
				? parseInt(process.env.VUE_APP_KA_DANGER_RANGE)
				: -70
			setInterval(() => {
				let state = false
				for (const device_name in this.nearby_devices) {
					if (this.nearby_devices[device_name].is_danger) state = true
				}
				this.in_danger = state
			}, 5000)
			if (this.keep_away_active) return
			const options = {
				acceptAllAdvertisements: true,
				keepRepeatedDevices: true,
			}
			try {
				await navigator.bluetooth.requestLEScan(options)
				navigator.bluetooth.addEventListener('advertisementreceived', event => {
					this.nearby_devices[event.device.id] = {
						name: event.device.name,
						rssi: event.rssi,
						tx_power: event.txPower,
						is_danger: event.rssi > danger_rssi,
					}
				})

				this.keep_away_active = true
			} catch (error) {
				if (error.code == 0)
					this.setAlert('Please grant simplelearn bluetooth permission')
				if (error.code == 18) return
				if (error.code == undefined)
					this.setAlert(
						'Bluetooth is not enabled yet. Please allow #enable-experimental-web-platform-features from chrome://flags'
					)
				console.log(`There was an issue with Keepaway ${error}`)
			}
		},
	},
	computed: {
		...mapGetters(['theme', 'user', 'loading', 'alert', 'force_load']),
		page_title: function() {
			if (this.$breadcrumbs[0])
				return this.$breadcrumbs[this.$breadcrumbs.length - 1].name
			return ''
		},
		bg_color: function() {
			if (this.user)
				if (this.theme.dark) return '#202020'
				else return '#f8f8fc'
			return this.theme.secondary
		},
		alert_margin: function() {
			return this.$refs.nav_bar ? this.$refs.nav_bar.$el.clientWidth : 0
		},
		show_keepaway: function() {
			const is_chrome =
				/Chrome/.test(navigator.userAgent) &&
				/Google Inc/.test(navigator.vendor)
			const is_desktop =
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				) == false
			return is_desktop && is_chrome
		},
	},
	mounted() {
		if (
			this.user == null &&
			!['Login', 'Reset Password'].includes(this.$route.name) &&
			!window.location.href.includes('reset')
		)
			this.$router.push({ name: 'Login' })

		this.keep_away_active = false
		if (this.user) this.startKeepAway()
		if (this.theme.dark) this.dark_mode_active = true
		else this.dark_mode_active = false
		if (this.user.is_admin == true)
			this.navItems.unshift(
				{
					title: 'Create',
					icon: 'mdi-pencil',
					route: 'Create',
				},
				{
					title: 'Statistics',
					icon: 'mdi-chart-areaspline',
					route: 'Statistics',
				}
			)
		else if (this.navItems[0].title == 'Create') this.navItems.splice(0, 2)
	},
	watch: {
		user: function(new_val) {
			if (new_val == null) this.$router.push({ name: 'Login' })
		},
		'user.is_admin': function(new_val) {
			if (new_val == true)
				this.navItems.unshift(
					{
						title: 'Create',
						icon: 'mdi-pencil',
						route: 'Create',
					},
					{
						title: 'Statistics',
						icon: 'mdi-chart-areaspline',
						route: 'Statistics',
					}
				)
			else if (this.navItems[0].title == 'Create') this.navItems.splice(0, 2)
		},

		alert: function(new_val, old_val) {
			if (new_val.message && old_val.message == null) {
				setTimeout(() => {
					this.alert_height = this.$refs.alert.$el.clientHeight
					this.window_height += this.alert_height
				}, 50)
			} else this.window_height -= this.alert_height
		},
		'theme.dark': function(new_val) {
			this.dark_mode_active = new_val
		},
	},
}
</script>

<style>
.v-text-field__slot input {
	color: #333 !important;
	font-size: 0.9rem;
}

.theme--light.v-select .v-select__selection--comma {
	align-self: center;
	font-size: 0.9rem;
	color: #333;
}

.v-list-item__title {
	font-size: 0.9rem !important;
}

.alt-bg {
	background-color: red !important;
}
.nav-active {
	background-color: #011b3a;
	border-left: 3px solid turquoise;
}
#app,
#footer {
	background-color: #f8f8fc;
}
.uppercase {
	text-transform: uppercase;
}
.bold {
	font-weight: bold;
}
.font-small {
	font-size: 0.8rem !important;
}
.font-normal {
	font-size: 1rem !important;
}
.breadcrumb-item a {
	text-decoration: none;
}
.breadcrumb-item a::before {
	padding-right: 15px;
	color: grey;
}
.breadcrumb-item a::after {
	padding-right: 3px;
}
.breadcrumb {
	padding-left: 18px !important;
}
.breadcrumb-item::before {
	content: '/';
	padding-right: 15px;
	color: grey;
}
.custom-round {
	border-radius: 15px !important;
}
.col-red {
	/** color: red !important; **/
}
.main-content-container {
	max-width: 1200px;
}
.line-breaks {
	white-space: pre-wrap;
}
.max-200 {
	max-width: 200px !important;
}
.no-bullet-point {
	list-style-type: none;
}
.truncated-text {
	max-width: 200px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.duration {
	color: #002147;
	background-color: #edf6ff !important;
}
.score {
	color: #005800;
	background-color: #e5ffe5 !important;
}
.subject {
	background-color: #f8f8fc !important;
}
.duration #icon {
	color: #002147;
}

.v-stepper {
	box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0), 0px 0px 0px 0px rgba(0, 0, 0, 0),
		0px 0px 0px 0px rgba(0, 0, 0, 0) !important;
}
.v-stepper--vertical {
	padding-bottom: 10px !important;
}

.v-stepper__step__step {
	background: rgba(0, 0, 0, 0.12) !important;
}
* {
	/* font-size: 100%; */
	/* font-family: 'Times New Roman', Times, serif; */
	/* font-family: 'Lucida Sans Unicode', 'Lucida Grande', sans-serif; */
	/* font-family: Camphor, Open Sans, Segoe UI, sans-serif; */
	font-family: georgia, times, 'Times New Roman', serif;
	text-rendering: optimizeLegibility;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}
.col-white {
	color: white !important;
}
.big-card-image {
	border-top-left-radius: 4px;
	border-bottom-left-radius: 4px;
}
.big-card-image-alt {
	border-top-right-radius: 4px;
	border-bottom-right-radius: 4px;
}
.border {
	border: 1px solid lightgrey;
}
.outer-container {
	max-width: 1400px !important;
}
.bg-green {
	background-color: #009000;
	border-color: #009000 !important;
}
.bg-white {
	background-color: white !important;
	border-color: white !important;
}
.bg-black {
	background-color: #101010 !important;
	color: white !important;
}
.font-tiny {
	font-size: 10px !important;
}

.font-under-normal {
	font-size: 0.9rem !important;
}
.stepper {
	cursor: pointer;
}
.cursor-pointer {
	cursor: pointer;
}
.cursor-default {
	cursor: default;
}
.cursor-all-scroll {
	cursor: all-scroll;
}
.rounded-top {
	border-top-left-radius: 8px !important;
	border-top-right-radius: 8px !important;
}
.col-whitesmoke {
	color: whitesmoke !important;
}
.col-lightgrey {
	color: lightgrey !important;
}
.flip-y {
	transform: scaleY(-1);
}
</style>
